/**
 * PATH: packages/job-hub/src/jobs/qa/classification.ts
 * FILE_ID: JOBHUB-QA-CLASSIFY-001
 * PURPOSE: QA classification vocabulary (category / project type / NCC building
 *          class / trades) + a pure keyword AI pre-selection for the Jobs Hub
 *          "set up QA from quotes" flow.
 * WHY:     Operator (2026-06-18): "auto select the industry which we had and
 *          classifications in the QA hub so we are merging the logics of both".
 *          The QA hub's New Inspection wizard pre-selects category/class/trades
 *          from job text; this brings the same logic into the job QA setup so a
 *          generated QA carries the right classification.
 * FLOW:
 * ┌──────────────────────┐   ┌──────────────────────┐   ┌─────────────────────┐
 * │ job text (site/client│ → │ keyword match → class │ → │ {category, class,   │
 * │ /scope/type/tags)    │   │ + trades + confidence │   │  trades, reason}    │
 * └──────────────────────┘   └──────────────────────┘   └─────────────────────┘
 * NOTE: This intentionally mirrors apps/qa/src/lib/inspection/* +
 *       new-inspection/ai-preselection.ts. apps/qa is another agent's zone, so
 *       this is a self-contained job-hub copy (leaner — no icons/tooltips).
 *       TODO: consolidate into a shared @quotech package once ownership allows.
 */

export type QaCategory = 'residential' | 'commercial' | 'infrastructure'
export type QaProjectType = 'new_construction' | 'renovation' | 'remediation'

export interface QaOption<T extends string = string> {
  value: T
  label: string
}

export const QA_CATEGORIES: QaOption<QaCategory>[] = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'infrastructure', label: 'Infrastructure' },
]

export const QA_PROJECT_TYPES: QaOption<QaProjectType>[] = [
  { value: 'new_construction', label: 'New construction' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'remediation', label: 'Remediation' },
]

/** Australian NCC building classes (value = stored token, label = short hint). */
export const QA_BUILDING_CLASSES: QaOption[] = [
  { value: 'Class 1a', label: '1a · House' },
  { value: 'Class 1b', label: '1b · Boarding' },
  { value: 'Class 2', label: '2 · Apartments' },
  { value: 'Class 3', label: '3 · Hotel/accom' },
  { value: 'Class 4', label: '4 · Resi in other' },
  { value: 'Class 5', label: '5 · Office' },
  { value: 'Class 6', label: '6 · Retail' },
  { value: 'Class 7a', label: '7a · Car park' },
  { value: 'Class 7b', label: '7b · Warehouse' },
  { value: 'Class 8', label: '8 · Factory/lab' },
  { value: 'Class 9a', label: '9a · Healthcare' },
  { value: 'Class 9b', label: '9b · Assembly' },
  { value: 'Class 9c', label: '9c · Aged care' },
  { value: 'Class 10a', label: '10a · Non-habitable' },
]

export const QA_TRADES: string[] = [
  'Waterproofing', 'Structural', 'Fire Safety', 'Electrical', 'Plumbing',
  'HVAC', 'Civil', 'Roofing', 'Cladding', 'Carpentry', 'Concreting',
  'Painting', 'Tiling',
]

export interface JobClassificationContext {
  /** Free job text (site address, client, scope, processing types, notes). */
  text?: string | null
  /** Explicit job type when known (e.g. "civil", "remediation"). */
  type?: string | null
  /** Job tags. */
  tags?: string[] | null
}

export interface QaClassification {
  category: QaCategory
  projectType: QaProjectType
  buildingClasses: string[]
  tradeTags: string[]
}

export interface QaClassificationSuggestion extends QaClassification {
  confidence: number
  reason: string
}

const INFRA_KW = ['hospital', 'school', 'university', 'college', 'healthcare', 'medical', 'clinic', 'aged care', 'nursing', 'public', 'government', 'council', 'library', 'community', 'church', 'assembly', 'theatre', 'stadium', 'spark', 'consortium', 'education', 'childcare', 'kindergarten']
const RESI_KW = ['house', 'home', 'residence', 'dwelling', 'apartment', 'unit', 'townhouse', 'villa', 'duplex', 'granny flat', 'residential', 'domestic']
const COMM_KW = ['office', 'retail', 'shop', 'shopping', 'warehouse', 'factory', 'industrial', 'commercial', 'business', 'corporate', 'carpark', 'car park', 'storage']

const TRADE_PATTERNS: [string[], string][] = [
  [['waterproof', 'water proof', 'membrane', 'wet area', 'bathroom', 'shower', 'laundry', 'balcony', 'basement', 'tanking'], 'Waterproofing'],
  [['structural', 'concrete', 'steel', 'foundation', 'beam', 'column', 'slab', 'framing', 'diaphragm', 'underslab'], 'Structural'],
  [['fire', 'sprinkler', 'hydrant', 'smoke', 'alarm', 'egress', 'emergency'], 'Fire Safety'],
  [['electric', 'power', 'wiring', 'switchboard', 'lighting', 'amp'], 'Electrical'],
  [['plumb', 'pipe', 'drainage', 'sewer', 'water supply', 'sanitary'], 'Plumbing'],
  [['hvac', 'air condition', 'aircon', 'mechanical', 'ventilation', 'heating', 'cooling', 'duct'], 'HVAC'],
  [['civil', 'earthworks', 'road', 'pavement', 'landscap'], 'Civil'],
  [['roof', 'gutter', 'fascia', 'soffit', 'flashing'], 'Roofing'],
  [['cladding', 'facade', 'external wall', 'render', 'curtain wall'], 'Cladding'],
  [['tile', 'tiling', 'screed'], 'Tiling'],
]

/**
 * Suggest a QA classification from job text. Pure I->O: same input always
 * yields the same suggestion, so it's unit-tested without a DOM. Confidence is
 * a coarse 0..0.95 signal used only to badge the UI ("AI pre-selected").
 */
export function suggestClassification(ctx: JobClassificationContext | null | undefined): QaClassificationSuggestion {
  const type = (ctx?.type || '').toLowerCase()
  const tags = (ctx?.tags || []).map((t) => String(t).toLowerCase())
  const allText = `${ctx?.text || ''} ${type} ${tags.join(' ')}`.toLowerCase()
  const reasons: string[] = []
  let confidence = 0.3

  let category: QaCategory = 'commercial'
  if (INFRA_KW.some((kw) => allText.includes(kw)) || type === 'civil' || type === 'infrastructure') {
    category = 'infrastructure'; reasons.push('Infrastructure project'); confidence += 0.2
  } else if (RESI_KW.some((kw) => allText.includes(kw))) {
    category = 'residential'; reasons.push('Residential project'); confidence += 0.2
  } else if (COMM_KW.some((kw) => allText.includes(kw)) || type === 'commercial') {
    category = 'commercial'; reasons.push('Commercial project'); confidence += 0.1
  }

  let projectType: QaProjectType = 'new_construction'
  if (type === 'remediation' || /remediation|defect|repair|rectif/.test(allText)) {
    projectType = 'remediation'; reasons.push('Remediation work'); confidence += 0.1
  } else if (/renovation|refurbish|retrofit|upgrade|fit.?out/.test(allText)) {
    projectType = 'renovation'; reasons.push('Renovation project'); confidence += 0.1
  }

  let buildingClass: string | null = null
  const m: [RegExp, string, string][] = [
    [/\b(hospital|medical|clinic|healthcare|health.?care|surgery|pathology)\b/, 'Class 9a', 'Healthcare facility'],
    [/\b(school|university|college|child.?care|kindergarten|church|community|library|theatre|cinema|stadium|spark|consortium)\b/, 'Class 9b', 'Education/assembly'],
    [/\b(aged.?care|nursing|retirement|elderly)\b/, 'Class 9c', 'Aged care facility'],
    [/\b(apartment|unit|flat|multi.?unit|multi.?residential)\b/, 'Class 2', 'Multi-unit residential'],
    [/\b(house|home|dwelling|residence)\b/, 'Class 1a', 'Single dwelling'],
    [/\b(hotel|motel|accommodation|resort)\b/, 'Class 3', 'Hotel/accommodation'],
    [/\b(office|professional|corporate|business)\b/, 'Class 5', 'Office building'],
    [/\b(shop|retail|cafe|restaurant|showroom|market)\b/, 'Class 6', 'Retail premises'],
    [/\b(carpark|car.?park|parking)\b/, 'Class 7a', 'Car park'],
    [/\b(warehouse|storage|distribution|logistics)\b/, 'Class 7b', 'Warehouse/storage'],
    [/\b(factory|manufacturing|laboratory|workshop|production)\b/, 'Class 8', 'Factory/industrial'],
  ]
  for (const [re, cls, why] of m) {
    if (re.test(allText)) { buildingClass = cls; reasons.push(why); confidence += 0.3; break }
  }
  if (!buildingClass) {
    buildingClass = category === 'residential' ? 'Class 1a' : category === 'infrastructure' ? 'Class 9b' : 'Class 5'
    reasons.push('Default for category')
  }

  const tradeTags: string[] = []
  for (const [keywords, trade] of TRADE_PATTERNS) {
    if (keywords.some((kw) => allText.includes(kw)) && !tradeTags.includes(trade)) {
      tradeTags.push(trade)
      if (trade === 'Waterproofing' || trade === 'Structural') confidence += 0.1
    }
  }
  if (category === 'infrastructure' && !tradeTags.includes('Fire Safety')) tradeTags.push('Fire Safety')
  if (tradeTags.length === 0) {
    if (category === 'residential') tradeTags.push('Structural', 'Electrical', 'Plumbing')
    else if (category === 'infrastructure') tradeTags.push('Structural', 'Fire Safety', 'Electrical')
    else tradeTags.push('Electrical', 'Fire Safety', 'HVAC')
  }

  return {
    category,
    projectType,
    buildingClasses: [buildingClass],
    tradeTags: [...new Set(tradeTags)],
    confidence: Math.min(confidence, 0.95),
    reason: reasons.length ? reasons.join(', ') : 'Default selections',
  }
}

/** Build the POST payload the from-quote-sheet endpoint understands. */
export function classificationPayload(c: QaClassification): {
  category: QaCategory
  project_type: QaProjectType
  building_classes: string[]
  trade_tags: string[]
} {
  return {
    category: c.category,
    project_type: c.projectType,
    building_classes: c.buildingClasses,
    trade_tags: c.tradeTags,
  }
}
